import { Plus, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';

import { cx } from './internal/cx.js';

export interface AutocompleteOption {
  value: string;
  label: string;
  /** Optional secondary line shown under the label in the list. */
  description?: string;
  /** Optional code-style line shown above the label in the list, e.g. an identifier. */
  caption?: string;
}

export interface AutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Id of the input, so a visible `<label htmlFor>` can name it. */
  id?: string;
  /** Accessible name when no visible label. */
  'aria-label'?: string;
  /** Id of helper text that describes the input. */
  'aria-describedby'?: string;
  /** Filter options as the user types. Default true. */
  filterOptions?: boolean;
  /** Shown in place of the list when what the user typed matches nothing. */
  emptyMessage?: string;
  /** Verb shown at the end of each option, e.g. "Add", when picking acts rather than selects. */
  optionAction?: string;
  /** A search field: magnifier on the left instead of the dropdown chevron. */
  search?: boolean;
}

function optionMatchesQuery(opt: AutocompleteOption, query: string): boolean {
  const lower = query.toLowerCase();
  return (
    opt.label.toLowerCase().includes(lower) ||
    opt.value.toLowerCase().includes(lower) ||
    (opt.caption?.toLowerCase().includes(lower) ?? false) ||
    (opt.description?.toLowerCase().includes(lower) ?? false)
  );
}

/** Filterable combobox for typeahead selection. */
export function Autocomplete({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  filterOptions = true,
  emptyMessage,
  optionAction,
  search = false,
}: AutocompleteProps): JSX.Element {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** True only after the user edits the input — gates filtering so a seeded selected label does not collapse the list. */
  const [typing, setTyping] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const selected = options.find((opt) => opt.value === value);
  const displayValue = open ? query : (selected?.label ?? '');

  const filtered = useMemo(() => {
    if (!filterOptions || !typing || !query.trim()) return options;
    return options.filter((opt) => optionMatchesQuery(opt, query));
  }, [filterOptions, options, query, typing]);

  const activeOptionId =
    open && filtered.length > 0 && highlightIndex >= 0
      ? `${listboxId}-option-${highlightIndex}`
      : undefined;

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== undefined) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  function openList(): void {
    setOpen(true);
    setTyping(false);
    setQuery(selected?.label ?? '');
    setHighlightIndex(selected ? Math.max(0, options.indexOf(selected)) : 0);
  }

  function selectOption(opt: AutocompleteOption): void {
    onChange(opt.value);
    setQuery('');
    setTyping(false);
    setOpen(false);
    setHighlightIndex(0);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      openList();
      return;
    }
    if (!open) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && filtered[highlightIndex]) {
      event.preventDefault();
      selectOption(filtered[highlightIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
      setTyping(false);
    }
  }

  const showEmpty = open && typing && query.trim() !== '' && filtered.length === 0;

  return (
    <div className={cx('nrx-autocomplete', search && 'nrx-autocomplete--search', className)}>
      {search ? <Search className="nrx-autocomplete__search-icon" aria-hidden="true" /> : null}
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={open && filtered.length > 0 ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className="input nrx-autocomplete__input"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={(event) => {
          openList();
          // The selected label is seeded as the query; selecting it lets the first keystroke
          // replace it instead of appending to it, which would match nothing.
          event.currentTarget.select();
        }}
        // Focus alone does not reopen the list after a selection closes it while focus stays put.
        onClick={() => {
          if (!open) openList();
        }}
        onBlur={() => {
          if (blurTimeoutRef.current !== undefined) {
            clearTimeout(blurTimeoutRef.current);
          }
          blurTimeoutRef.current = setTimeout(() => setOpen(false), 150);
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setTyping(true);
          setOpen(true);
          setHighlightIndex(0);
        }}
        onKeyDown={handleKeyDown}
      />
      {search ? null : <span className="nrx-autocomplete__chevron" aria-hidden="true" />}
      {open && filtered.length > 0 ? (
        <ul id={listboxId} role="listbox" className="nrx-autocomplete__list">
          {filtered.map((opt, index) => (
            <li
              key={opt.value}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={opt.value === value}
              className={cx(
                'nrx-autocomplete__option',
                index === highlightIndex && 'nrx-autocomplete__option--highlighted',
                opt.value === value && 'nrx-autocomplete__option--selected',
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                selectOption(opt);
              }}
            >
              <span className="nrx-autocomplete__option-text">
                {opt.caption ? (
                  <span className="nrx-autocomplete__option-caption">{opt.caption}</span>
                ) : null}
                <span className="nrx-autocomplete__option-label">{opt.label}</span>
                {opt.description ? (
                  <span className="nrx-autocomplete__option-description">{opt.description}</span>
                ) : null}
              </span>
              {optionAction ? (
                <span className="nrx-autocomplete__option-action">
                  <Plus aria-hidden="true" />
                  {optionAction}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {showEmpty && emptyMessage ? (
        <div role="status" className="nrx-autocomplete__empty">
          {emptyMessage}
        </div>
      ) : null}
    </div>
  );
}
